// reCAPTCHA v3 helper - invisible scoring + per-action tokens.
//
// Set `VITE_RECAPTCHA_SITE_KEY` in apps/web/.env.local to enable. With no key
// set, every helper is a no-op and forms still submit (useful for local dev
// against the emulators). Backend verification with the secret key has to be
// added in the Cloud Functions layer; that part is intentionally out of scope
// here.

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

const SITE_KEY: string | undefined = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

let loaded = false;
let loadingPromise: Promise<void> | null = null;

export function loadRecaptcha(): Promise<void> {
  if (!SITE_KEY) return Promise.resolve();
  if (loaded) return Promise.resolve();
  if (loadingPromise) return loadingPromise;

  loadingPromise = new Promise<void>((resolve, reject) => {
    if (document.getElementById('grecaptcha-script')) {
      // Another caller already injected it - just wait for grecaptcha.
      const wait = () => {
        if (window.grecaptcha) { window.grecaptcha.ready(() => { loaded = true; resolve(); }); }
        else setTimeout(wait, 100);
      };
      wait();
      return;
    }
    const script = document.createElement('script');
    script.id = 'grecaptcha-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => { loaded = true; resolve(); });
      } else {
        reject(new Error('grecaptcha script loaded but global is missing'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
    document.head.appendChild(script);
  });
  return loadingPromise;
}

export async function executeRecaptcha(action: string): Promise<string | undefined> {
  if (!SITE_KEY) return undefined;
  try {
    await loadRecaptcha();
    if (!window.grecaptcha) return undefined;
    return await window.grecaptcha.execute(SITE_KEY, { action });
  } catch (err) {
    console.warn('reCAPTCHA execute failed', err);
    return undefined;
  }
}

export const recaptchaEnabled = Boolean(SITE_KEY);
